/**
 * Targets div-form case-detail blocks (`obituary`, `order_of_service` and
 * `family_appreciation_message`
 * via `caseDetailDiv` + `handleTextBlock`, and `additional_family` via
 * `handleAdditionalFamily`) and removes them cleanly when the user's
 * keystroke empties or selects them.
 *
 * Three flows are handled:
 *
 * 1. **Highlighted-then-deleted (single backspace)**: When the user clicks
 *    on a case-detail block, `highlightDynamicTextPlugin` calls
 *    `range.selectNodeContents(div)`. Because the div is
 *    `contenteditable="false"`, the browser refuses to delete on backspace
 *    or only clears partial content. We detect that exact selection state
 *    on `keydown`, `preventDefault`, and remove the div ourselves.
 *
 * 2. **Highlighted-then-cut (Cmd+X / context-menu Cut)**: Same selection
 *    shape as flow 1, but the user fires a `cut` event instead of a
 *    delete keystroke. The browser's default cut on a `contenteditable="false"`
 *    wrapper leaves the same orphaned empty shell as Backspace. We listen
 *    on `cut` directly, populate the clipboard ourselves, and remove the
 *    div in the same `undoManager.transact`.
 *
 * 3. **Emptied via other deletes (Cmd+A backspace)**: On `keydown` we
 *    snapshot which case-detail divs are currently NON-empty. On `keyup`
 *    we re-check that same set: any that became empty as a *result of this
 *    keystroke* are removed. We never touch case-detail divs that were
 *    already empty before the keystroke — that avoids removing stale state
 *    when the user is editing somewhere unrelated.
 *
 * Scope is intentionally narrow — only the case-detail values listed in
 * `CASE_DETAIL_DIV_VALUES` are touched. Other case details (spans like
 * `name_of_deceased`, inline case details) are left untouched. To extend
 * coverage to a new div-form case detail, add its value to the constant.
 */

import { regEx } from '@/utils/global/regEx'

const CASE_DETAIL_DIV_VALUES = [
  'obituary',
  'order_of_service',
  'family_appreciation_message',
  'additional_family',
] as const
type CaseDetailDivValue = (typeof CASE_DETAIL_DIV_VALUES)[number]

const CASE_DETAIL_DIV_SELECTOR = CASE_DETAIL_DIV_VALUES.map(
  (value) => `div[data-case-detail="${value}"]`,
).join(', ')

const isCaseDetailDivValue = (
  value: string | null,
): value is CaseDetailDivValue =>
  value !== null &&
  (CASE_DETAIL_DIV_VALUES as readonly string[]).includes(value)

const isCaseDetailDivEmpty = (div: HTMLElement): boolean => {
  // Fast paths — avoid the full textContent scan for the two states we hit
  // most often after a Backspace inside a case-detail block.
  if (!div.firstChild) return true
  if (div.childNodes.length === 1 && div.firstChild.nodeName === 'BR') {
    return true
  }
  // String.trim() strips standard whitespace including NBSP, so we only
  // need to remove zero-width characters before checking.
  const text = (div.textContent ?? '')
    .replace(regEx.zeroWidthCharacters, '')
    .trim()
  return text.length === 0
}

const snapshotNonEmptyCaseDetailDivs = (editor: Editor): Set<HTMLElement> => {
  const set = new Set<HTMLElement>()
  editor.dom.select(CASE_DETAIL_DIV_SELECTOR).forEach((div) => {
    if (!isCaseDetailDivEmpty(div as HTMLElement)) {
      set.add(div as HTMLElement)
    }
  })
  return set
}

const removeNewlyEmptiedCaseDetailDivs = (
  editor: Editor,
  candidates: Set<HTMLElement>,
): boolean => {
  // Scope the connectedness check to the editor body, not the whole document —
  // a div moved out of the editor (e.g., by another plugin or drag-drop) would
  // still pass `isConnected` but wouldn't be part of undo history.
  const editorBody = editor.getBody()
  const newlyEmpty: HTMLElement[] = []
  candidates.forEach((div) => {
    if (editorBody.contains(div) && isCaseDetailDivEmpty(div)) {
      newlyEmpty.push(div)
    }
  })

  if (newlyEmpty.length === 0) return false

  // Wrap in an undo transaction so Cmd+Z restores the case-detail div.
  editor.undoManager.transact(() => {
    newlyEmpty.forEach((div) => editor.dom.remove(div))
  })

  editor.fire('change')
  editor.nodeChanged()

  return true
}

/**
 * Returns the case-detail div if (and only if) the current selection is
 * precisely the result of `range.selectNodeContents(div)` — i.e., the user
 * clicked on a case-detail block and `highlightDynamicTextPlugin` highlighted
 * its contents.
 */
const getFullySelectedCaseDetailDiv = (editor: Editor): HTMLElement | null => {
  const range = editor.selection?.getRng?.()
  if (!range) return null
  if (range.startContainer !== range.endContainer) return null

  const container = range.startContainer
  if (!(container instanceof HTMLElement)) return null
  if (container.tagName !== 'DIV') return null
  if (!isCaseDetailDivValue(container.getAttribute('data-case-detail'))) {
    return null
  }
  if (range.startOffset !== 0) return null
  if (range.endOffset !== container.childNodes.length) return null

  return container
}

const placeCursorNear = (editor: Editor, removedDiv: HTMLElement) => {
  const previousSibling =
    removedDiv.previousElementSibling as HTMLElement | null
  const nextSibling = removedDiv.nextElementSibling as HTMLElement | null

  if (previousSibling) {
    editor.selection.setCursorLocation(
      previousSibling,
      previousSibling.childNodes.length,
    )
  } else if (nextSibling) {
    editor.selection.setCursorLocation(nextSibling, 0)
  } else {
    // Obit was the only child — fall back to the editor body so TinyMCE's
    // selection doesn't dangle pointing at a removed node.
    editor.selection.setCursorLocation(editor.getBody(), 0)
  }
}

const isDeleteKey = (e: KeyboardEvent): boolean =>
  (e.key === 'Backspace' || e.key === 'Delete') && !e.isComposing

export const removeEmptyCaseDetailDivPlugin = ({
  editor,
}: {
  editor: Editor
}) => {
  // Per-editor closure: tracks which case-detail divs were non-empty at the
  // start of the current Backspace/Delete keystroke, so keyup can compare.
  let nonEmptyAtKeydown = new Set<HTMLElement>()

  editor.on('keydown', (e: KeyboardEvent) => {
    if (!isDeleteKey(e)) return

    // Path 1: user clicked a case-detail block so highlightDynamicTextPlugin
    // selected its contents. The browser will block deletion inside
    // contenteditable false, so we remove the div ourselves.
    const caseDetailDiv = getFullySelectedCaseDetailDiv(editor)
    if (caseDetailDiv) {
      e.preventDefault()
      // Wrap in an undo transaction so Cmd+Z restores the case-detail div.
      editor.undoManager.transact(() => {
        placeCursorNear(editor, caseDetailDiv)
        editor.dom.remove(caseDetailDiv)
      })
      editor.fire('change')
      editor.nodeChanged()
      nonEmptyAtKeydown = new Set()
      return
    }

    // Path 2: snapshot every currently-non-empty case-detail div so keyup
    // can detect any that THIS keystroke empties (e.g., Cmd+A backspace).
    nonEmptyAtKeydown = snapshotNonEmptyCaseDetailDivs(editor)
  })

  editor.on('keyup', (e: KeyboardEvent) => {
    if (!isDeleteKey(e)) return

    const candidates = nonEmptyAtKeydown
    nonEmptyAtKeydown = new Set()
    if (candidates.size === 0) return

    removeNewlyEmptiedCaseDetailDivs(editor, candidates)
  })

  // Cut (Cmd+X / Ctrl+X / context-menu Cut) on a click-highlighted case-detail
  // div: the browser's default cut leaves the same orphaned empty shell as
  // Backspace, so we own the clipboard write and the removal here.
  editor.on('cut', (e: ClipboardEvent) => {
    const caseDetailDiv = getFullySelectedCaseDetailDiv(editor)
    if (!caseDetailDiv) return

    // Populate the clipboard so paste still works. The browser's default
    // cut on a contenteditable="false" wrapper either copies nothing or
    // copies partial content, so we set both text/html and text/plain
    // ourselves before preventing the default.
    if (e.clipboardData) {
      e.clipboardData.setData('text/html', caseDetailDiv.outerHTML)
      e.clipboardData.setData('text/plain', caseDetailDiv.textContent ?? '')
    }
    e.preventDefault()

    // Wrap in an undo transaction so Cmd+Z restores the case-detail div.
    editor.undoManager.transact(() => {
      placeCursorNear(editor, caseDetailDiv)
      editor.dom.remove(caseDetailDiv)
    })
    editor.fire('change')
    editor.nodeChanged()
    nonEmptyAtKeydown = new Set()
  })

  // Clear the snapshot on blur so stale DOM references don't leak across
  // focus changes. Without this, a synthetic keyup (IME, a11y tools, browser
  // autofill) after blur-return could act on a stale snapshot.
  editor.on('blur', () => {
    nonEmptyAtKeydown = new Set()
  })
}

export const __testing = {
  CASE_DETAIL_DIV_VALUES,
  CASE_DETAIL_DIV_SELECTOR,
  isCaseDetailDivEmpty,
  snapshotNonEmptyCaseDetailDivs,
  removeNewlyEmptiedCaseDetailDivs,
  getFullySelectedCaseDetailDiv,
}

export default removeEmptyCaseDetailDivPlugin
